import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Slider } from '../ui/slider';
import { API, errorText } from '../governance_v2/constants';
import { ACTIVITY_STATUS, SELECT_CLS, fmtDate } from './constants';

const L = ({ children }) => <span className="mb-1 block text-xs font-medium text-slate-500">{children}</span>;

const Comments = ({ companyId, activity, onAdded }) => {
  const [text, setText] = useState('');
  const add = useMutation({
    mutationFn: () => axios.post(`${API}/companies/${companyId}/activities/${activity.id}/comments`, { text }),
    onSuccess: () => { setText(''); onAdded(); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <div className="space-y-2 border-t border-slate-200 pt-3 dark:border-slate-700" data-testid="activity-comments">
      <p className="flex items-center gap-1 text-sm font-semibold"><MessageSquare className="h-4 w-4" />Comentarios</p>
      {(activity.comments || []).map((c) => (
        <div key={c.id} className="rounded-md bg-slate-50 p-2 text-sm dark:bg-slate-800/60">
          <p className="text-xs text-slate-500">{c.author_name || c.author} · {new Date(c.at).toLocaleString('es')}</p><p>{c.text}</p>
        </div>
      ))}
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Escribe un comentario…" data-testid="activity-comment-input" />
        <Button size="sm" onClick={() => add.mutate()} disabled={!text.trim() || add.isPending} data-testid="activity-comment-send">Enviar</Button>
      </div>
    </div>
  );
};

/** activity=null → create. staffMode → only status/progress + comments. */
const ActivityDialog = ({ companyId, projectId, activity, activities, members, staffMode, onClose }) => {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState(activity ? { ...activity, assignee_user_id: activity.assignee_user_id || '' }
    : { title: '', description: '', assignee_user_id: '', start_date: today, end_date: today, status: 'pendiente', progress: 0, depends_on: [] });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['project', companyId, projectId] }); qc.invalidateQueries({ queryKey: ['projects', companyId] }); qc.invalidateQueries({ queryKey: ['portfolio', companyId] }); qc.invalidateQueries({ queryKey: ['my-activities', companyId] }); };
  const save = useMutation({
    mutationFn: () => {
      const body = staffMode ? { status: f.status, progress: f.progress } : {
        title: f.title, description: f.description, assignee_user_id: f.assignee_user_id || null, start_date: f.start_date,
        end_date: f.end_date, status: f.status, progress: f.progress, depends_on: f.depends_on };
      return activity ? axios.patch(`${API}/companies/${companyId}/activities/${activity.id}`, body)
        : axios.post(`${API}/companies/${companyId}/projects/${projectId}/activities`, body);
    },
    onSuccess: () => { toast.success(activity ? 'Actividad actualizada' : 'Actividad creada'); refresh(); onClose(); },
    onError: (e) => toast.error(errorText(e)),
  });
  const del = useMutation({
    mutationFn: () => axios.delete(`${API}/companies/${companyId}/activities/${activity.id}`),
    onSuccess: () => { toast.success('Actividad eliminada'); refresh(); onClose(); },
    onError: (e) => toast.error(errorText(e)),
  });
  const others = activities.filter((a) => a.id !== activity?.id);
  const toggleDep = (id) => setF({ ...f, depends_on: f.depends_on.includes(id) ? f.depends_on.filter((d) => d !== id) : [...f.depends_on, id] });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="activity-dialog">
        <DialogHeader><DialogTitle>{activity ? activity.title : 'Nueva actividad'}</DialogTitle></DialogHeader>
        {staffMode ? <p className="text-sm text-slate-500">{fmtDate(f.start_date)} → {fmtDate(f.end_date)}{f.description ? ` · ${f.description}` : ''}</p> : (
          <div className="grid grid-cols-2 gap-3">
            <label className="col-span-2"><L>Título</L><Input value={f.title} onChange={set('title')} data-testid="activity-form-title" /></label>
            <label className="col-span-2"><L>Descripción</L><Textarea rows={2} value={f.description} onChange={set('description')} data-testid="activity-form-description" /></label>
            <label><L>Inicio</L><Input type="date" value={f.start_date} onChange={set('start_date')} data-testid="activity-form-start" /></label>
            <label><L>Fin</L><Input type="date" value={f.end_date} onChange={set('end_date')} data-testid="activity-form-end" /></label>
            <label className="col-span-2"><L>Responsable</L><select className={SELECT_CLS} value={f.assignee_user_id} onChange={set('assignee_user_id')} data-testid="activity-form-assignee">
              <option value="">Sin asignar</option>{members.map((m) => <option key={m.id} value={m.id}>{m.full_name} ({m.role})</option>)}</select></label>
            {others.length > 0 && <div className="col-span-2"><L>Depende de</L><div className="flex flex-wrap gap-2">{others.map((a) => (
              <button type="button" key={a.id} onClick={() => toggleDep(a.id)} data-testid={`activity-form-dep-${a.id}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors duration-150 ${f.depends_on.includes(a.id) ? 'border-sky-500 bg-sky-500/15 text-sky-700 dark:text-sky-300' : 'border-slate-300 text-slate-500 dark:border-slate-700'}`}>{a.title}</button>))}</div></div>}
          </div>
        )}
        <div className="grid grid-cols-2 items-end gap-3">
          <label><L>Estado</L><select className={SELECT_CLS} value={f.status} onChange={set('status')} data-testid="activity-form-status">
            {Object.entries(ACTIVITY_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></label>
          <div><L>Avance: <b data-testid="activity-form-progress-value">{f.progress}%</b></L>
            <Slider value={[f.progress]} max={100} step={5} onValueChange={([v]) => setF({ ...f, progress: v })} data-testid="activity-form-progress" /></div>
        </div>
        {activity && <Comments companyId={companyId} activity={activity} onAdded={refresh} />}
        <div className="flex justify-between gap-2 pt-2">
          <div>{activity && !staffMode && <Button variant="ghost" className="text-red-600" onClick={() => window.confirm('¿Eliminar esta actividad?') && del.mutate()} data-testid="activity-delete-btn">Eliminar</Button>}</div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} data-testid="activity-form-cancel">Cerrar</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || (!staffMode && f.title.trim().length < 3)} data-testid="activity-form-save">{save.isPending ? 'Guardando…' : 'Guardar'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityDialog;
